/**
 * @Description 营业收入
 */

import { FC, useEffect } from 'react';
import TableModule from './TableModule';
import {
  SaleTargetNames,
  OperatingIncomeConfig,
  OperatingIncomeNames,
  CoreInfoRelateNames,
  LeaseCostNames,
  isCollaborationModeDiscount,
  isCollaborationModeHighValue,
} from '../ts-config';
import { Form } from 'antd';
import { isNotEmpty } from '@lhb/func';
import {
  fieldNameSaleTarget,
  fieldNameOperatingIncome,
  fieldNameLeaseCost
} from './fieldname';
import { fixIntParam, fixPrecisionParam } from './transfer2value';

const OperatingIncome: FC<any> = ({
  form,
  formItemData,
  disabled,
  collaborationModeOptions,
  onChange
}) => {

  // 定制组件之外的「合作模式」的值
  const collaborationMode: any = Form.useWatch(
    CoreInfoRelateNames.collaborationMode,
    form
  );

  // 终端销售指标（第一年）
  const saleTargetFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.saleTarget),
    form
  );
  // 终端销售指标（第二年）
  const saleTargetSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.saleTarget),
    form
  );

  // 老品销售吊牌销售金额（第一年）
  const oldProductAmountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.oldProductAmount),
    form
  );
  // 老品销售吊牌销售金额（第二年）
  const oldProductAmountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.oldProductAmount),
    form
  );

  // 新品销售吊牌销售金额（第一年）
  const newProductAmountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.newProductAmount),
    form
  );
  // 新品销售吊牌销售金额（第二年）
  const newProductAmountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.newProductAmount),
    form
  );

  // 老品进货折率（第一年）
  const oldProductPurchaseDiscountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.oldProductPurchaseDiscount),
    form
  );
  // 老品进货折率（第二年）
  const oldProductPurchaseDiscountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.oldProductPurchaseDiscount),
    form
  );

  // 新品进货折率（第一年）
  const newProductPurchaseDiscountFirst: any = Form.useWatch(
    fieldNameSaleTarget(true, SaleTargetNames.newProductPurchaseDiscount),
    form
  );
  // 新品销售吊牌销售金额（第二年）
  const newProductPurchaseDiscountSecond: any = Form.useWatch(
    fieldNameSaleTarget(false, SaleTargetNames.newProductPurchaseDiscount),
    form
  );

  // 商场联营扣点（第一年）
  const discountRateFirst: any = Form.useWatch(
    fieldNameLeaseCost(true, LeaseCostNames.discountRate),
    form
  );
  // 商场联营扣点（第二年）
  const discountRateSecond: any = Form.useWatch(
    fieldNameLeaseCost(false, LeaseCostNames.discountRate),
    form
  );

  // 营业收入（第一年）
  const operatingIncomeFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.operatingIncome),
    form
  );
  // 营业收入（第二年）
  const operatingIncomeSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.operatingIncome),
    form
  );

  // 商品成本（第一年）
  const costOfGoodsFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.costOfGoods),
    form
  );
  // 商品成本（第二年）
  const costOfGoodsSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.costOfGoods),
    form
  );

  // 财务毛利额（第一年）
  const financialGrossProfitFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.financialGrossProfit),
    form
  );
  // 财务毛利额（第二年）
  const financialGrossProfitSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.financialGrossProfit),
    form
  );

  // 财务毛利率（第一年）
  const financialGrossProfitMarginFirst: any = Form.useWatch(
    fieldNameOperatingIncome(true, OperatingIncomeNames.financialGrossProfitMargin),
    form
  );
  // 财务毛利率（第二年）
  const financialGrossProfitMarginSecond: any = Form.useWatch(
    fieldNameOperatingIncome(false, OperatingIncomeNames.financialGrossProfitMargin),
    form
  );

  /**
   * @description 营业收入
   */
  useEffect(() => {
    const name = fieldNameOperatingIncome(true, OperatingIncomeNames.operatingIncome);
    const val: any = calcOperatingIncome(collaborationMode, saleTargetFirst, discountRateFirst);
    form.setFieldValue(name, val);
  }, [collaborationMode, saleTargetFirst, discountRateFirst]);

  useEffect(() => {
    const name = fieldNameOperatingIncome(false, OperatingIncomeNames.operatingIncome);
    const val: any = calcOperatingIncome(collaborationMode, saleTargetSecond, discountRateSecond);
    form.setFieldValue(name, val);
  }, [collaborationMode, saleTargetSecond, discountRateSecond]);

  /**
   * @description 营业收入
   * @param collaborationMode 合作模式
   * @param saleTarget 终端销售指标
   * @param discountRate 商场联营扣点
   * @return
   *
   * 租赁：终端销售指标/1.13
      扣点：终端销售指标/1.13*（1-商场联营扣点）
      取高：比较两个值的，取高值
      注意：这里判断依据需要取定制组件之外的「合作模式」的值
   */
  const calcOperatingIncome = (collaborationMode, saleTarget, discountRate) => {
    let val: any = null;
    const selOpt = collaborationModeOptions.find((itm: any) => itm.id === collaborationMode);
    if (isCollaborationModeDiscount(selOpt?.name)) { // 扣点
      val = calcModeDiscount({ saleTarget, discountRate });
    } else if (isCollaborationModeHighValue(selOpt?.name)) { // 取高
      val = calcModeHighValue({ saleTarget, discountRate });
    } else { // 选择租赁、及未选时，都当作选中租赁
      val = calcModeRent({ saleTarget });
    }
    return val;
  };

  /**
   * @description 营业收入：合作模式为【租赁】时
   *    租赁：终端销售指标/1.13
   * @param saleTarget 终端销售指标
   * @return
   */
  const calcModeRent = ({ saleTarget }) => {
    let val: any = null;
    if (isNotEmpty(saleTarget)) {
      val = fixIntParam(+saleTarget / 1.13);
    }
    return val;
  };

  /**
   * @description 营业收入：合作模式为【扣点】时
   *    扣点：终端销售指标/1.13*（1-商场联营扣点）
   * @param saleTarget 终端销售指标
   * @param discountRate 商场联营扣点
   * @return
   */
  const calcModeDiscount = ({ saleTarget, discountRate }) => {
    let val: any = null;
    if (isNotEmpty(saleTarget) && isNotEmpty(discountRate)) {
      const leaseVal: any = fixIntParam(+saleTarget / 1.13);
      val = fixIntParam(leaseVal * (1 - discountRate / 100.0));
    }
    return val;
  };

  /**
   * @description 营业收入：合作模式为【取高】时
   * @param saleTarget 终端销售指标
   * @param discountRate 商场联营扣点
   * @return
   *
   * 租赁：终端销售指标/1.13
      扣点：终端销售指标/1.13*（1-商场联营扣点）
      取高：比较两个值的，取高值
      注意：这里判断依据需要取定制组件之外的「合作模式」的值
   */
  const calcModeHighValue = ({ saleTarget, discountRate }) => {
    let val: any = null;

    const leaseVal: any = calcModeRent({ saleTarget });
    const discountVal: any = calcModeDiscount({ saleTarget, discountRate });
    if (isNotEmpty(leaseVal) && isNotEmpty(discountVal)) {
      val = Math.max(leaseVal, discountVal);
    } else if (isNotEmpty(leaseVal)) {
      val = leaseVal;
    } else if (isNotEmpty(discountVal)) {
      val = discountVal;
    }
    return val;
  };

  /**
   * @description 商品成本
   */
  useEffect(() => {
    const name = fieldNameOperatingIncome(true, OperatingIncomeNames.costOfGoods);
    const val: any = calcCostOfGoods(oldProductAmountFirst, oldProductPurchaseDiscountFirst, newProductAmountFirst, newProductPurchaseDiscountFirst);
    form.setFieldValue(name, val);
  }, [oldProductAmountFirst, oldProductPurchaseDiscountFirst, newProductAmountFirst, newProductPurchaseDiscountFirst]);

  useEffect(() => {
    const name = fieldNameOperatingIncome(false, OperatingIncomeNames.costOfGoods);
    const val: any = calcCostOfGoods(oldProductAmountSecond, oldProductPurchaseDiscountSecond, newProductAmountSecond, newProductPurchaseDiscountSecond);
    form.setFieldValue(name, val);
  }, [oldProductAmountSecond, oldProductPurchaseDiscountSecond, newProductAmountSecond, newProductPurchaseDiscountSecond]);

  /**
   * @description 商品成本
   * @param oldProductAmount 合作模式
   * @param oldProductPurchaseDiscount 终端销售指标
   * @param newProductAmount 商场联营扣点
   * @param newProductPurchaseDiscount 商场联营扣点
   * @return
   *
   * （老品销售吊牌销售金额*老品进货折率/1.13）+（新品销售吊牌销售金额*新品进货折率/1.13）
   */
  const calcCostOfGoods = (oldProductAmount, oldProductPurchaseDiscount, newProductAmount, newProductPurchaseDiscount) => {
    let val: any = null;
    if (isNotEmpty(oldProductAmount) && isNotEmpty(oldProductPurchaseDiscount) && isNotEmpty(newProductAmount) && isNotEmpty(newProductPurchaseDiscount)) {
      val = fixIntParam((+oldProductAmount) * (+oldProductPurchaseDiscount / 100) / 1.13 + (+newProductAmount) * (+newProductPurchaseDiscount / 100) / 1.13);
    }
    return val;
  };


  /**
   * @description 财务毛利额
   */
  useEffect(() => {
    const name = fieldNameOperatingIncome(true, OperatingIncomeNames.financialGrossProfit);
    const val: any = calcFinancialGrossProfit(operatingIncomeFirst, costOfGoodsFirst);
    form.setFieldValue(name, val);
  }, [operatingIncomeFirst, costOfGoodsFirst]);

  useEffect(() => {
    const name = fieldNameOperatingIncome(false, OperatingIncomeNames.financialGrossProfit);
    const val: any = calcFinancialGrossProfit(operatingIncomeSecond, costOfGoodsSecond);
    form.setFieldValue(name, val);
  }, [operatingIncomeSecond, costOfGoodsSecond]);

  /**
   * @description 财务毛利额：营业收入-商品成本
   * @param operatingIncome 营业收入
   * @param costOfGoods 商品成本
   * @return
   *
   */
  const calcFinancialGrossProfit = (operatingIncome, costOfGoods) => {
    let val: any = null;
    if (isNotEmpty(operatingIncome) && isNotEmpty(costOfGoods)) {
      val = fixIntParam((+operatingIncome) - (+costOfGoods));
    }
    return val;
  };


  /**
   * @description 财务毛利率
   */
  useEffect(() => {
    const name = fieldNameOperatingIncome(true, OperatingIncomeNames.financialGrossProfitMargin);
    const val: any = calcFinancialGrossProfitMargin(financialGrossProfitFirst, operatingIncomeFirst);
    form.setFieldValue(name, val);
  }, [financialGrossProfitFirst, operatingIncomeFirst]);

  useEffect(() => {
    const name = fieldNameOperatingIncome(false, OperatingIncomeNames.financialGrossProfitMargin);
    const val: any = calcFinancialGrossProfitMargin(financialGrossProfitSecond, operatingIncomeSecond);
    form.setFieldValue(name, val);
  }, [financialGrossProfitSecond, operatingIncomeSecond]);

  /**
   * @description 财务毛利率：财务毛利额/营业收入
   * @param financialGrossProfit 财务毛利额
   * @param operatingIncome 营业收入
   * @return
   *
   */
  const calcFinancialGrossProfitMargin = (financialGrossProfit, operatingIncome) => {
    let val: any = null;
    if (isNotEmpty(financialGrossProfit) && operatingIncome) {
      val = fixPrecisionParam((+financialGrossProfit) * 100.0 / (+operatingIncome));
    }
    return val;
  };

  /**
   * @description 特殊处理，自定义组件相关的外部属性collaborationMode【合作模式】变动时，会影响到自动计算结果，需要触发计算
   */
  useEffect(() => {
    onChange && onChange();
  }, [operatingIncomeFirst, operatingIncomeSecond, costOfGoodsFirst, costOfGoodsSecond, financialGrossProfitFirst, financialGrossProfitSecond, financialGrossProfitMarginFirst, financialGrossProfitMarginSecond]);

  return (
    <TableModule
      moduleConfig={OperatingIncomeConfig}
      form={form}
      formItemData={formItemData}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

export default OperatingIncome;
